import React from 'react';
import { Page, Text, View, Document, StyleSheet, Font, Image } from '@react-pdf/renderer';
import PropTypes from 'prop-types';
import { dayLabel, localeFor, translations } from '../../utils/translations';
import { dayOrder, jointParentNames, normalizeDay } from '../Data/familyCompat';

// Hook-free translator for the PDF tree: @react-pdf renders outside the
// React context tree, so the language arrives as a prop and we read the
// table directly. Missing keys fall back to English, then to the key itself.
const makeT = (language) => (key) => {
  const lookup = (lang) => key.split('.').reduce((value, k) => value?.[k], translations[lang]);
  return lookup(language) ?? lookup('en') ?? key;
};

// --- Font Registration ---
Font.register({
  family: 'DejaVu Sans',
  fonts: [
    { src: '/fonts/DejaVuSans.ttf' },
    { src: '/fonts/DejaVuSans-Bold.ttf', fontWeight: 'bold' },
    { src: '/fonts/DejaVuSans-Oblique.ttf', fontStyle: 'italic' },
    { src: '/fonts/DejaVuSans-BoldOblique.ttf', fontWeight: 'bold', fontStyle: 'italic' },
  ],
});

// --- Styling ---
const styles = StyleSheet.create({
  page: {
    fontFamily: 'DejaVu Sans',
    padding: 35,
    backgroundColor: '#ffffff',
    color: '#333',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 25,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#eaeaea',
  },
  logo: {
    maxWidth: 150,
    maxHeight: 150,
    marginRight: 20,
  },
  headerText: {
    flexDirection: 'column',
    flex: 1,
    alignItems: 'flex-end',
    textAlign: 'right',
  },
  familyTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    textAlign: 'right',
    maxWidth: '100%',
    overflow: 'hidden',
  },
  familySubtitle: {
    fontSize: 14,
    color: '#666',
    textAlign: 'right',
  },
  lastUpdated: {
    fontSize: 10,
    color: '#888',
    textAlign: 'right',
    marginTop: 3,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 15,
    marginTop: 20,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  // Student View
  studentCard: {
    marginBottom: 20,
  },
  studentHeader: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
    backgroundColor: '#f0f0f0',
    padding: 8,
    borderRadius: 3,
  },
  // Activity View
  activitySection: {
    marginBottom: 20,
  },
  // Date View
  daySection: {
    marginBottom: 20,
  },
  // Table Styles
  table: { 
    display: "table", 
    width: "auto", 
    borderStyle: "solid", 
    borderWidth: 1, 
    borderRightWidth: 0, 
    borderBottomWidth: 0,
    borderColor: '#d3d3d3',
    borderRadius: 6,
    overflow: 'hidden',
    marginBottom: 10,
  }, 
  tableRow: { 
    margin: "auto", 
    flexDirection: "row" 
  }, 
  tableColHeader: { 
    width: "25%", 
    borderStyle: "solid", 
    borderWidth: 1, 
    borderLeftWidth: 0, 
    borderTopWidth: 0,
    backgroundColor: '#f0f0f0',
    padding: 5,
    fontSize: 10,
    fontWeight: 'bold',
  }, 
  tableColHeaderWide: {
    width: "50%", 
    borderStyle: "solid", 
    borderWidth: 1, 
    borderLeftWidth: 0, 
    borderTopWidth: 0,
    backgroundColor: '#f0f0f0',
    padding: 5,
    fontSize: 10,
    fontWeight: 'bold',
  },
  tableCol: { 
    width: "25%", 
    borderStyle: "solid", 
    borderWidth: 1, 
    borderLeftWidth: 0, 
    borderTopWidth: 0,
    padding: 5,
    fontSize: 10,
  },
  tableColWide: {
    width: "50%",
    borderStyle: "solid", 
    borderWidth: 1, 
    borderLeftWidth: 0, 
    borderTopWidth: 0,
    padding: 5,
    fontSize: 10,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 35,
    right: 35,
    textAlign: 'center',
    color: 'grey',
    fontSize: 8,
  },
});

// --- Reusable Components ---
const PageHeader = ({ data, language = 'en' }) => {
  const t = makeT(language);

  // Same rule as the on-screen header: a shared surname reads once
  const joint = jointParentNames(data.parent1.name, data.parent2.name);
  const fullName = joint
    ? `${joint.given1} & ${joint.given2} ${joint.surname}`
    : `${data.parent1.name}${data.parent2.name ? ` & ${data.parent2.name}` : ''}`;
  
  // More aggressive font size reduction based on name length
  let fontSize = 22;
  if (fullName.length > 50) {
    fontSize = 14;
  } else if (fullName.length > 35) {
    fontSize = 16;
  } else if (fullName.length > 25) {
    fontSize = 18;
  }

  return (
    <View style={styles.header}>
      <Image style={styles.logo} src="/aos-logo.png" />
      <View style={styles.headerText}>
        <Text style={[
          styles.familyTitle, 
          { fontSize: fontSize }
        ]}>
          {fullName}
        </Text>
        <Text style={styles.familySubtitle}>{t('report.familyInformation')}</Text>
        {data.lastUpdated && (
          <Text style={styles.lastUpdated}>
            {t('main.lastUpdated')}: {new Date(data.lastUpdated).toLocaleDateString(localeFor(language))}
          </Text>
        )}
      </View>
    </View>
  );
};

const PageFooter = () => (
  null
);

// --- View-Specific PDF Layouts ---

const StudentViewPDF = ({ data, language = 'en' }) => {
  const t = makeT(language);

  const getActivityTranslationKey = (activityName) => {
    const mapping = {
      "Sunday School": "sundaySchool",
      "Russian School": "russianSchool",
      "Children's Choir": "childrensChoir"
    };
    return mapping[activityName] || activityName;
  };

  return (
    <View>
      {data.children.map(child => (
        <View key={child.id} style={styles.studentCard}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <Text style={[styles.studentHeader, {fontWeight: 'bold'}]}>{child.name}</Text>
            <Text style={{ fontSize: 10, color: '#666' }}><Text style={{fontWeight: 'bold'}}>{t('details.birthday')}:</Text> {child.birthdate}</Text>
          </View>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <Text style={styles.tableColHeader}>{t('report.activity')}</Text>
              <Text style={styles.tableColHeader}>{t('report.details')}</Text>
              <Text style={styles.tableColHeaderWide}>{t('report.teachersLeaders')}</Text>
            </View>
            {Object.entries(child.activities).map(([name, details]) => (
              <View key={name} style={styles.tableRow}>
                <Text style={styles.tableCol}>{t(`activities.${getActivityTranslationKey(name)}`)}</Text>
                <View style={styles.tableCol}>
                  <Text><Text style={{fontWeight: 'bold'}}>{name === "Children's Choir" ? t('details.choirNumber') : t('details.class')}:</Text> {name === "Children's Choir" ? details.choirNumber : details.class}</Text>
                  <Text><Text style={{fontWeight: 'bold'}}>{t('details.room')}:</Text> {details.classroom || details.choirRoom}</Text>
                  <Text><Text style={{fontWeight: 'bold'}}>{t('report.day')}:</Text> {dayLabel(details.day, language)}</Text>
                </View>
                <Text style={styles.tableColWide}>{name === "Children's Choir" ? details.choirLeaders?.join(', ') : details.teachers?.join(', ')}</Text>
              </View>
            ))}
          </View>
        </View>
      ))}
    </View>
  );
};

const ActivityViewPDF = ({ data, language = 'en' }) => {
  const t = makeT(language);

  const getActivityTranslationKey = (activityName) => {
    const mapping = {
      "Sunday School": "sundaySchool",
      "Russian School": "russianSchool",
      "Children's Choir": "childrensChoir"
    };
    return mapping[activityName] || activityName;
  };

  const activities = {};
  data.children.forEach(child => {
    Object.entries(child.activities).forEach(([activityName, details]) => {
      if (!activities[activityName]) {
        activities[activityName] = [];
      }
      activities[activityName].push({ 
        childName: child.name, 
        details, 
        birthdate: child.birthdate 
      });
    });
  });

  return (
    <View>
      {Object.entries(activities).map(([activityName, participants]) => (
        <View key={activityName} style={styles.activitySection}>
          <Text style={styles.studentHeader}>{t(`activities.${getActivityTranslationKey(activityName)}`)}</Text>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <Text style={styles.tableColHeader}>{t('report.student')}</Text>
              <Text style={styles.tableColHeader}>{t('report.details')}</Text>
              <Text style={styles.tableColHeaderWide}>{activityName === "Children's Choir" ? t('details.choirLeaders') : t('details.teachers')}</Text>
            </View>
            {participants.map(p => (
              <View key={p.childName} style={styles.tableRow}>
                <View style={styles.tableCol}>
                  <Text style={{fontWeight: 'bold'}}>{p.childName}</Text>
                  <Text><Text style={{fontWeight: 'bold'}}>{t('details.birthday')}:</Text> {p.birthdate}</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text><Text style={{fontWeight: 'bold'}}>{activityName === "Children's Choir" ? t('details.choirNumber') : t('details.class')}:</Text> {activityName === "Children's Choir" ? p.details.choirNumber : p.details.class}</Text>
                  <Text><Text style={{fontWeight: 'bold'}}>{t('details.room')}:</Text> {p.details.classroom || p.details.choirRoom}</Text>
                  <Text><Text style={{fontWeight: 'bold'}}>{t('report.day')}:</Text> {dayLabel(p.details.day, language)}</Text>
                </View>
                <Text style={styles.tableColWide}>{activityName === "Children's Choir" ? p.details.choirLeaders?.join(', ') : p.details.teachers?.join(', ')}</Text>
              </View>
            ))}
          </View>
        </View>
      ))}
    </View>
  );
};

const DateViewPDF = ({ data, language = 'en' }) => {
  const t = makeT(language);

  const getActivityTranslationKey = (activityName) => {
    const mapping = {
      "Sunday School": "sundaySchool",
      "Russian School": "russianSchool",
      "Children's Choir": "childrensChoir"
    };
    return mapping[activityName] || activityName;
  };

  // Group by the canonical weekday so a Russian and an English spelling of the
  // same day land together; unrecognized values keep their raw text.
  const days = {};
  data.children.forEach(child => {
    Object.entries(child.activities).forEach(([activityName, details]) => {
      const day = normalizeDay(details.day) || details.day || '';
      if (!days[day]) {
        days[day] = [];
      }
      days[day].push({ 
        childName: child.name, 
        activityName, 
        details, 
        birthdate: child.birthdate 
      });
    });
  });

  const sortedDays = Object.keys(days).sort((a, b) => dayOrder(a) - dayOrder(b));

  return (
    <View>
      {sortedDays.map((day) => {
        const activities = days[day];
        
        return (
          <View key={day} style={styles.daySection}>
            <Text style={styles.studentHeader}>{dayLabel(day, language)}</Text>
            <View style={styles.table}>
              <View style={styles.tableRow}>
                <Text style={styles.tableColHeader}>{t('report.student')}</Text>
                <Text style={styles.tableColHeader}>{t('report.activity')}</Text>
                <Text style={styles.tableColHeaderWide}>{t('report.details')}</Text>
              </View>
              {activities.map(a => (
                <View key={`${a.childName}-${a.activityName}`} style={styles.tableRow}>
                  <View style={styles.tableCol}>
                    <Text style={{fontWeight: 'bold'}}>{a.childName}</Text>
                    <Text><Text style={{fontWeight: 'bold'}}>{t('details.birthday')}:</Text> {a.birthdate}</Text>
                  </View>
                  <Text style={styles.tableCol}>{t(`activities.${getActivityTranslationKey(a.activityName)}`)}</Text>
                  <View style={styles.tableColWide}>
                    <Text><Text style={{fontWeight: 'bold'}}>{a.activityName === "Children's Choir" ? t('details.choirNumber') : t('details.class')}:</Text> {a.activityName === "Children's Choir" ? a.details.choirNumber : a.details.class}</Text>
                    <Text><Text style={{fontWeight: 'bold'}}>{t('details.room')}:</Text> {a.details.classroom || a.details.choirRoom}</Text>
                    <Text><Text style={{fontWeight: 'bold'}}>{a.activityName === "Children's Choir" ? t('details.choirLeaders') : t('details.teachers')}:</Text> {a.activityName === "Children's Choir" ? a.details.choirLeaders?.join(', ') : a.details.teachers?.join(', ')}</Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        );
      })}
    </View>
  );
};

// --- Main Document Component ---
const ReportDocument = ({ data, viewMode, language = 'en' }) => {
  if (!data) {
    return (
      <Document>
        <Page style={styles.page}><Text>{makeT(language)('report.noData')}</Text></Page>
      </Document>
    );
  }

  const renderView = () => {
    switch (viewMode) {
      case 'student':
        return <StudentViewPDF data={data} language={language} />;
      case 'activity':
        return <ActivityViewPDF data={data} language={language} />;
      case 'date':
        return <DateViewPDF data={data} language={language} />;
      default:
        return <StudentViewPDF data={data} language={language} />;
    }
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <PageHeader data={data} language={language} />
        {renderView()}
        <PageFooter />
      </Page>
    </Document>
  );
};

// --- Prop Types ---
const dataPropTypes = PropTypes.shape({
  parent1: PropTypes.shape({ name: PropTypes.string }),
  parent2: PropTypes.shape({ name: PropTypes.string }),
  children: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    activities: PropTypes.object,
  })),
});

ReportDocument.propTypes = {
  data: dataPropTypes,
  viewMode: PropTypes.string,
  language: PropTypes.string,
};

PageHeader.propTypes = { data: dataPropTypes, language: PropTypes.string };
StudentViewPDF.propTypes = { data: dataPropTypes, language: PropTypes.string };
ActivityViewPDF.propTypes = { data: dataPropTypes, language: PropTypes.string };
DateViewPDF.propTypes = { data: dataPropTypes, language: PropTypes.string };

export default ReportDocument;
